import { ChatsApi } from "@/api/chats";
import { GetChatMessagesRequest } from "@/api/chats/types";
import { queryClient, QueryKey } from "@/utils";
import { useInfiniteQuery } from "@tanstack/react-query";

const PER_PAGE = 30;

export const getChatMessagesQuery = ({
  chatId,
}: Pick<GetChatMessagesRequest, "chatId">) => {
  return queryClient.fetchInfiniteQuery({
    queryKey: [QueryKey.CHAT_MESSAGES, chatId],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      ChatsApi.getChatMessages({
        chatId,
        perPage: PER_PAGE,
        cursor: pageParam,
      }),
    initialPageParam: undefined,
  });
};

export const useGetChatMessages = ({
  chatId,
}: Pick<GetChatMessagesRequest, "chatId">) => {
  const { data, isPending, error, fetchNextPage } = useInfiniteQuery({
    queryKey: [QueryKey.CHAT_MESSAGES, chatId],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      ChatsApi.getChatMessages({
        perPage: PER_PAGE,
        cursor: pageParam,
        chatId: chatId,
      }),
    staleTime: 0,
    initialPageParam: undefined,
    enabled: !!chatId,
    getNextPageParam: (lastPage) => lastPage.meta.nextCursor,
    select: ({ pages }) => {
      const data = pages.flatMap(({ data }) => data);
      const meta = pages[0].meta;

      return { meta, data };
    },
  });

  return {
    messages: data,
    chatMessagesError: error,
    isChatMessagesLoading: isPending,
    fetchNextChatMessagesPage: fetchNextPage,
  };
};
